import React, {useRef, useState} from 'react';
import {CardElement, Elements, injectStripe, StripeProvider} from 'react-stripe-elements';
import {
    Col,
    ControlLabel,
    FormControl,
    FormGroup,
    Glyphicon,
    OverlayTrigger,
    ProgressBar,
    Row,
    Tooltip
} from "react-bootstrap";
import * as moment from "moment";
import './StripeCard.css'
import {browserHistory} from "react-router";
import StripePowered from "../../../../images/Powered by Stripe - black.svg";
import Dollars from "../../../../components/Dollars";
import {defaultDateFormat} from "../../../../common/commonHandlers";
import {useMediaQuery} from '../../../../utilities/hooks/useMediaQuery';

const createOptions = () => {
    return {
        style: {
            base: {
                width: '100%',
                fontSize: '16px',
                color: '#424770',
                fontFamily: 'Open Sans, sans-serif',
                letterSpacing: '0.025em',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#c23d4b',
            },
        }
    }
};

const _CardForm = ({
                       stripeCredentials,
                       stripe,
                       hideModal,
                       loadCredentials,
                       subscriptionData,
                       subscriptionAction,
                       activeUsers,
                       selectedPrice,
                       firstUserPrice
                   }) => {
    const smMobile = useMediaQuery(450);
    const savedCardLast4 = stripeCredentials?.stripe_subscription?.last4
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [name, setName] = useState("")
    const [stripePaymentMethod, setStripePaymentMethod] = useState()
    const [isCompleted, setIsCompleted] = useState(false)
    const [useSavedCard, setUseSavedCard] = useState(!!savedCardLast4)
    const cardElement = useRef()
    const numberOfUsers = activeUsers
    const totalPrice = (activeUsers - 1) * selectedPrice + firstUserPrice

    const subscriptionId = stripeCredentials?.stripe_subscription?.subscription_id
    const subscriptionStatus = stripeCredentials?.stripe_subscription?.status
    const isSubscriptionActive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing'
    const subscriptionUserCount = stripeCredentials?.stripe_subscription?.user_count
    const autoRenew = stripeCredentials?.stripe_subscription?.auto_renew
    const activeLabel = `${activeUsers}/${subscriptionUserCount ? subscriptionUserCount : 0}`
    const expirationDesc = autoRenew ? "Next payment date:" : "Your subscription will expire:"
    const tooltipDescriptions = {
        users: {
            title: 'Users',
            description: 'Number of users who will use the app'
        },
        name: {
            title: 'Name',
            description: 'Your firstname and lastname on credit card'
        },
        card: {
            title: 'Card',
            description: 'Your card number'
        }
    }

    const handleCardChange = async (e) => {
        if (e.complete) {
            const {error, paymentMethod} = await stripe.createPaymentMethod('card', cardElement.current._element);
            if (error) {
                setErrorMessage(error.message)
            } else {
                setStripePaymentMethod(paymentMethod)
                setIsCompleted(true)
            }
        } else {
            setIsCompleted(false)
        }
        e.error ? setErrorMessage(e.error.message) : setErrorMessage('')
    }

    const confirmPayment = clientSecret => stripe.handleCardPayment(clientSecret)

    const finishSubscription = () => {
        hideModal()
        loadCredentials()
        setIsLoading(false)
        browserHistory.push('/login')
    }

    const handleSubmit = e => {
        e.preventDefault();
        setIsLoading(true)
        setErrorMessage('')

        subscriptionData.user_count = numberOfUsers
        subscriptionData.name = name
        subscriptionAction.createSubscription(subscriptionData, useSavedCard ? null : stripePaymentMethod, response => {
            if (response.errors) {
                setErrorMessage(response.errors.join(' '))
                setIsLoading(false)
                return
            }

            if (!response.client_secret) {
                finishSubscription()
                return
            }

            confirmPayment(response.client_secret).then(result => {
                if (result.error) {
                    setErrorMessage(result.error.message)
                    setIsLoading(false)
                    return
                }

                finishSubscription()
            }).catch(error => {
                setErrorMessage(error.message)
                setIsLoading(false)
            })
        })
    };

    // TODO fix this, we have app tooltips
    const renderTooltip = (title, description) => (
        <Tooltip id="tooltip">
            <div className="text-left">
                <strong>{title}</strong><span> - {description}</span>
            </div>
        </Tooltip>
    );

    const generateNumberOfUsersInfo = () =>
        <FormGroup bsSize="small" className='credential_input'>
            <ControlLabel className='d-flex'>
                <p className='bold bottom0'><span className='colorRed mr-3'>*</span>SELECTED NUMBER OF
                    USERS:{numberOfUsers}</p>
                <OverlayTrigger placement={smMobile ? "bottom" : "right"}
                                overlay={renderTooltip(tooltipDescriptions.users.title, tooltipDescriptions.users.description)}>
                    <Glyphicon glyph="info-sign text-info" className="notification ml-4 pointer"/>
                </OverlayTrigger>
            </ControlLabel>
            <FormControl
                type='text'
                placeholder='Number of users'
                value={activeUsers}
                disabled={true}
            />
        </FormGroup>
    const generateCardInfo = () => {
        const last4 = stripeCredentials?.stripe_subscription?.last4;
        return last4 ? <Row className="card-info">
            <Col md={4}>
                Credit card:
            </Col>
            <Col md={6}>
                **** **** **** {last4}
            </Col>
        </Row> : null
    }
    const generateUsersProgressBar = () =>
        <div className='active-users-notitification'>
            <ProgressBar className='active-users-progress-bar' bsStyle="info"
                         now={(activeUsers / subscriptionUserCount) * 100}/>
            <p className='colorRed font10'>{activeLabel} users is actually active</p>
        </div>

    const generateSubscriptionNameField = () =>
        <FormGroup bsSize="medium" className='credential_input'>
            <ControlLabel className='d-flex'>
                <p className='bold bottom0'><span className='colorRed mr-3'>*</span>FULL NAME</p>
                <OverlayTrigger placement={smMobile ? "bottom" : "right"}
                                overlay={renderTooltip(tooltipDescriptions.name.title, tooltipDescriptions.name.description)}>
                    <Glyphicon glyph="info-sign text-info" className="notification ml-4 pointer"/>
                </OverlayTrigger>
            </ControlLabel>
            <FormControl
                type='text'
                placeholder='First and last name'
                value={name}
                onChange={e => setName(e.target.value)}
            />
        </FormGroup>

    const generateCardNumberField = () =>
        <FormGroup bsSize="small" className='credential_input'>
            <ControlLabel className='d-flex'>
                <p className='bold bottom0'><span className='colorRed mr-3'>*</span>CARD</p>
                <OverlayTrigger placement={smMobile ? "bottom" : "right"}
                                overlay={renderTooltip(tooltipDescriptions.card.title, tooltipDescriptions.card.description)}>
                    <Glyphicon glyph="info-sign text-info" className="notification ml-4 pointer"/>
                </OverlayTrigger>
            </ControlLabel>
            <CardElement
                ref={cardElement}
                onChange={(e) => handleCardChange(e)}
                {...createOptions()}
            />
        </FormGroup>

    const canSubmit = name.length > 0 && numberOfUsers > 0 && (useSavedCard || isCompleted)

    return (
        <div className="CardSubscritionDemo">
            {
                <form onSubmit={!isSubscriptionActive && handleSubmit}>
                    {subscriptionId && isSubscriptionActive && activeUsers ? <div>
                        {generateCardInfo()}
                        <Row>
                            {generateUsersProgressBar()}
                        </Row>
                    </div> : <div>
                        <Row>
                            {generateNumberOfUsersInfo()}
                        </Row>
                        <Row className='mt-40'>
                            {generateSubscriptionNameField()}
                        </Row>
                        {savedCardLast4 && <Row className='saved-card mt-40 padding-bottom-10'>
                            <span>Card ending in {savedCardLast4}</span>
                            <button type='button' onClick={() => setUseSavedCard(!useSavedCard)}>
                                {useSavedCard ? 'Use a different card' : 'Use saved card'}
                            </button>
                        </Row>}
                        {!useSavedCard && <Row className='mt-40'>
                            {generateCardNumberField()}
                        </Row>}
                        <Row className='textCenter'>
                            <FormGroup bsSize="small" className='credential_input stripe-powered'>
                                <img src={StripePowered} alt='Stripe' style={{width: 150}}/>
                            </FormGroup>
                        </Row>
                        <Row className='textCenter mt-40'>
                            <div className="error text-danger" role="alert">
                                <strong>{errorMessage}</strong>
                            </div>
                        </Row>
                    </div>}
                    <Row>
                        {subscriptionId && isSubscriptionActive && autoRenew ?
                            <button className='full-width' style={{background: '#DD1923'}}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsLoading(true)
                                        subscriptionAction.cancelSubscription({
                                            subscription_id: subscriptionId
                                        }, (res) => {
                                            res.subscription_canceled && loadCredentials()
                                            setIsLoading(false)
                                        })
                                    }}
                            >
                                {!isLoading ? <div><span>Cancel Subscription</span>
                                </div> : "Loading..."}
                            </button> : subscriptionId && isSubscriptionActive ?
                            <button className='full-width'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsLoading(true)
                                        subscriptionAction.updateSubscription({
                                            subscription_id: subscriptionId,
                                            auto_renew: true
                                        }, () => {
                                            loadCredentials()
                                            setIsLoading(false)
                                        })
                                    }}
                            >
                                {!isLoading ? <div><span>Resume Subscription</span>
                                </div> : "Loading..."}
                            </button>
                            :
                            <div className='text-right'>
                                <button type='submit' disabled={!canSubmit || isLoading}
                                        className={`full-width ${!canSubmit && 'subscribe-btn-disabled'}`}>
                                    {!isLoading ? <div><span>{canSubmit ? <strong>Pay <Dollars
                                        amount={totalPrice}/></strong> : `Subscribe`}</span>
                                    </div> : "Loading..."}
                                </button>
                            </div>
                        }
                        {subscriptionId && isSubscriptionActive &&
                            <div className='hright trial-version-expired'>
                                <p>{expirationDesc}</p>
                                <p className='ml-4'>{moment.unix(stripeCredentials?.stripe_subscription?.expire_date).format(defaultDateFormat)}</p>
                            </div>}
                    </Row>
                </form>
            }
        </div>
    );
}

const CardForm = injectStripe(_CardForm);

export const PaymentElement = ({
                                   stripeCredentials,
                                   hideModal,
                                   subscriptionData,
                                   loadCredentials,
                                   subscriptionAction,
                                   activeUsers,
                                   selectedPrice,
                                   firstUserPrice
                               }) => (
    <StripeProvider apiKey={stripeCredentials.stripe_publishable_key}>
        <Elements>
            <CardForm
                stripeCredentials={stripeCredentials}
                hideModal={hideModal}
                loadCredentials={loadCredentials}
                subscriptionData={subscriptionData}
                subscriptionAction={subscriptionAction}
                activeUsers={activeUsers}
                selectedPrice={selectedPrice}
                firstUserPrice={firstUserPrice}
            />
        </Elements>
    </StripeProvider>
);
